import React, { useState, useEffect } from 'react';
import { auth } from '../firebase';
import { apiGet, apiPost, apiUpload } from '../api';
import { onAuthStateChanged } from 'firebase/auth';

const Profile = () => {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);

  const fetchProfileData = async () => {
    try {
      const { profile } = await apiGet('/api/account/me');
      if (profile) setProfileData(profile);
      else setError("Agency profile not found for the current user.");
    } catch { setError("Failed to fetch profile data."); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (user) { setLoading(true); await fetchProfileData(); }
      else { setError("Please log in to view your profile."); setProfileData(null); setLoading(false); }
    });
    return () => unsubscribeAuth();
  }, []);

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!auth.currentUser) { setError("You must be logged in to upload an image."); return; }
    if (file.size > 2 * 1024 * 1024) { alert("File is too large. Please select an image under 2MB."); return; }
    if (!file.type.startsWith('image/')) { alert("Invalid file type. Please select an image."); return; }
    setUploading(true);
    try {
      const { url } = await apiUpload('agencies', file, `profile_${Date.now()}`);
      await apiPost('/api/account/profile-picture', { url });
      setProfileData(prev => ({ ...prev, profilePicture: url }));
      alert("Profile picture updated successfully!");
    } catch { setError("Failed to upload image. Please try again."); }
    finally { setUploading(false); }
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-20 gap-3">
      <div className="w-10 h-10 rounded-full border-4 border-white/10 border-l-[#ff4d6d] animate-spin" />
      <p className="text-sm text-white/40">Loading Profile...</p>
    </div>
  );

  if (error || !profileData) return (
    <div className="p-4">
      <div className="rounded-xl p-4 text-sm" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', color: '#f87171' }}>
        {error || "No profile data available."}
      </div>
    </div>
  );

  const initials = (profileData.name || 'AG').slice(0, 2).toUpperCase();

  const accountRows = [
    { icon: 'fa-user-circle', label: 'Username',     value: profileData.name,  color: '#c4b5fd', bg: 'rgba(196,181,253,0.1)' },
    { icon: 'fa-envelope',    label: 'Email Address', value: profileData.email, color: '#93c5fd', bg: 'rgba(147,197,253,0.1)' },
    { icon: 'fa-id-badge',    label: 'Agency ID',     value: profileData.ID,    color: '#ff4d6d', bg: 'rgba(255,77,109,0.1)'  },
  ];

  const financialRows = [
    { icon: 'fa-arrow-circle-down', label: 'Total Received',  value: (profileData.Received || 0).toLocaleString(), color: '#4ade80', bg: 'rgba(74,222,128,0.1)'  },
    { icon: 'fa-arrow-circle-up',   label: 'Total Sent',      value: (profileData.Send     || 0).toLocaleString(), color: '#fb923c', bg: 'rgba(251,146,60,0.1)'  },
    { icon: 'fa-wallet',            label: 'Current Balance', value: (profileData.Balance  || 0).toLocaleString(), color: '#38bdf8', bg: 'rgba(56,189,248,0.1)'  },
  ];

  return (
    <div className="p-4 space-y-4">

      {/* ── PROFILE HEADER (full width) ── */}
      <div className="rounded-2xl p-5 flex items-center gap-5 relative overflow-hidden" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
        {/* glow */}
        <div className="absolute top-0 left-0 w-56 h-full opacity-10 blur-3xl pointer-events-none" style={{ background: 'radial-gradient(circle,#ff4d6d,transparent)' }} />

        {/* Avatar + camera */}
        <div className="relative flex-shrink-0">
          {profileData.profilePicture ? (
            <img src={profileData.profilePicture} alt="Profile" className="w-20 h-20 rounded-2xl object-cover" style={{ border: '3px solid #ff4d6d' }} />
          ) : (
            <div className="w-20 h-20 rounded-2xl flex items-center justify-center text-white font-extrabold text-3xl" style={{ background: 'linear-gradient(135deg,#ff4d6d,#c026d3)', border: '3px solid rgba(255,77,109,0.5)' }}>
              {initials}
            </div>
          )}
          <label htmlFor="profile-upload" className="absolute -bottom-2 -right-2 w-8 h-8 rounded-xl flex items-center justify-center cursor-pointer text-white hover:opacity-90 transition-all" style={{ background: 'linear-gradient(135deg,#ff4d6d,#ff2d55)', border: '2px solid #0a0a1a' }}>
            {uploading ? <i className="fas fa-spinner fa-spin text-xs" /> : <i className="fas fa-camera text-xs" />}
            <input id="profile-upload" type="file" accept="image/*" className="hidden" onChange={handleImageUpload} disabled={uploading} />
          </label>
        </div>

        {/* Name + badge */}
        <div className="flex-1 min-w-0">
          <h2 className="font-extrabold text-white text-xl mb-1 truncate">{profileData.name}</h2>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-bold rounded-full px-3 py-1" style={{ background: 'rgba(255,77,109,0.15)', color: '#ff4d6d', border: '1px solid rgba(255,77,109,0.3)' }}>Agency</span>
            <span className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.4)' }}>{profileData.ID}</span>
          </div>
        </div>

        {/* Financial quick stats — right side, hidden on mobile */}
        <div className="hidden sm:flex items-center gap-3 flex-shrink-0">
          {financialRows.map((s, i) => (
            <div key={i} className="rounded-xl px-4 py-3 text-center min-w-[90px]" style={{ background: s.bg, border: `1px solid ${s.color}33` }}>
              <p className="font-extrabold text-lg leading-none" style={{ color: s.color }}>{s.value}</p>
              <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.4)' }}>{s.label.replace('Total ', '')}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── BOTTOM: Account Details + Financials side by side ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

        {/* Account Details */}
        <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="px-5 py-3.5 flex items-center gap-2" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <i className="fas fa-user-shield text-xs" style={{ color: '#ff4d6d' }} />
            <p className="text-xs font-bold uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.4)' }}>Account Details</p>
          </div>
          {accountRows.map((row, i, arr) => (
            <div key={i} className="flex items-center gap-3 px-5 py-4" style={{ borderBottom: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
              <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: row.bg }}>
                <i className={`fas ${row.icon} text-sm`} style={{ color: row.color }} />
              </div>
              <div className="min-w-0">
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>{row.label}</p>
                <p className="text-sm font-semibold text-white truncate">{row.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Financials */}
        <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="px-5 py-3.5 flex items-center gap-2" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <i className="fas fa-chart-line text-xs" style={{ color: '#4ade80' }} />
            <p className="text-xs font-bold uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.4)' }}>Financials</p>
          </div>
          {financialRows.map((row, i, arr) => (
            <div key={i} className="flex items-center gap-3 px-5 py-4" style={{ borderBottom: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
              <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: row.bg }}>
                <i className={`fas ${row.icon} text-sm`} style={{ color: row.color }} />
              </div>
              <div className="min-w-0">
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>{row.label}</p>
                <p className="font-extrabold text-lg leading-none mt-0.5" style={{ color: row.color }}>{row.value}</p>
              </div>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
};

export default Profile;
